import { useEffect, useMemo, useRef } from "react";
import { useAppStore } from "@/stores/appStore";
import { useOrganizations } from "@/hooks/api/useOrganizations";
import type { Organization } from "@/types/api";

/**
 * Hook for managing the currently selected organization.
 *
 * Selection priority:
 * 1. Previously selected org (from localStorage via Zustand)
 * 2. First owned org
 * 3. First org in the list (if none are owned)
 *
 * When user explicitly selects an org, it persists across sessions.
 */
export function useSelectedOrganization() {
  const { data, isLoading, error } = useOrganizations();
  const selectedOrgId = useAppStore((state) => state.org.selectedOrgId);
  const setSelectedOrgId = useAppStore((state) => state.setSelectedOrgId);

  // Memoize organizations to avoid dependency changes on every render
  const organizations = useMemo(() => data?.organizations ?? [], [data?.organizations]);

  // Determine the effective selected organization
  const selectedOrganization = useMemo<Organization | null>(() => {
    if (organizations.length === 0) {
      return null;
    }

    // 1. Check if previously selected org still exists in the list
    if (selectedOrgId) {
      const found = organizations.find((org) => org.id === selectedOrgId);
      if (found) {
        return found;
      }
    }

    // 2. Fall back to first org (backend returns owned orgs first via listOrgsForUser)
    // Since users can only own one org currently, the first one is the owned one
    return organizations[0];
  }, [organizations, selectedOrgId]);

  // Track if we've already auto-selected to avoid infinite loops
  const hasAutoSelectedRef = useRef(false);

  // Auto-select if no valid selection exists
  useEffect(() => {
    if (organizations.length === 0) {
      hasAutoSelectedRef.current = false;
      return;
    }

    // If current selection is invalid or missing, auto-select
    const currentSelectionValid =
      selectedOrgId && organizations.some((org) => org.id === selectedOrgId);

    if (!currentSelectionValid && !hasAutoSelectedRef.current) {
      // Auto-select first org
      hasAutoSelectedRef.current = true;
      setSelectedOrgId(organizations[0].id);
    } else if (currentSelectionValid) {
      hasAutoSelectedRef.current = true;
    }
  }, [organizations, selectedOrgId, setSelectedOrgId]);

  // Function to select an organization
  const selectOrganization = (orgId: string) => {
    const exists = organizations.some((org) => org.id === orgId);
    if (exists) {
      setSelectedOrgId(orgId);
    }
  };

  return {
    // The currently selected organization (resolved)
    selectedOrganization,
    // All available organizations
    organizations,
    // Function to change selection
    selectOrganization,
    // Loading state
    isLoading,
    // Error state
    error,
    // Whether user has any organizations
    hasOrganizations: organizations.length > 0,
  };
}
